import { Button, Collapse, Grid, Link, Stack, Typography } from '@mui/material';
import { find, startCase } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { usePostChangeSatelliteMode } from '@/api/device';
import { useSpotterModeChangeStatus } from '@/api/device/useSpotterModeChangeStatus';
import { DeviceRunMode, useDevice, useDeviceSubscriptionStatus } from '@/api/devices';
import { ActionDialog } from '@/components/ActionDialog';
import { FormSelect } from '@/components/form/FormSelect';
import { GenericSkeleton } from '@/components/loaders/GenericSkeleton';
import { SatelliteModeChangeText } from '@/components/spotter/configurationTab/PendingModeChangeAlert';
import { ResetUpdateButtonGroup } from '@/components/userAccount/ResetUpdateButtonGroup';
import { deviceModes, operatingModeToUpdateRateOptionsMap } from '@/config';
import { useDisclosure } from '@/hooks/useDisclosure';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { minorTextGrey } from '@/theme';

export type DeviceRegularSatelliteModes = keyof typeof operatingModeToUpdateRateOptionsMap;

type SatelliteConfigFormValues = {
    mode: DeviceRegularSatelliteModes;
    messagePeriod: string;
};

const formDefaultValues: SatelliteConfigFormValues = {
    mode: '',
    messagePeriod: '',
};

export const SatelliteDataSection = ({ readOnly }: { readOnly?: boolean }) => {
    const { isOpen: isConfirmDialogOpen, open: openConfirmDialog, close: closeConfirmDialog } = useDisclosure();
    const { mutateAsync: postChangeMode, isPending: pendingMutation } = usePostChangeSatelliteMode();
    const { data: device, isPending: isDevicesPending } = useDevice();
    const spotterId = device?.spotterId;
    const { data: subscriptionStatus } = useDeviceSubscriptionStatus({ spotterId });
    const { pendingModeSettingGroup } = useSpotterModeChangeStatus({ spotterId });

    const {
        control: formControl,
        reset: formReset,
        handleSubmit: handleSubmit,
        formState,
        watch: formWatch,
        getValues: formGetValues,
        setValue,
    } = useForm<SatelliteConfigFormValues>({
        defaultValues: formDefaultValues,
        mode: 'onChange',
    });
    const [, setOutOfRangeMode] = useState({ mode: '', messagePeriod: '', isOutOfRange: false });

    const isTelemetryEnabled = subscriptionStatus?.cellEnabled || subscriptionStatus?.satEnabled;
    const settingsLocked = !!(readOnly || pendingModeSettingGroup || !isTelemetryEnabled);

    const [modeValue] = formWatch(['mode']);
    useUnsavedChangesGuard(formState.isDirty, 'satellite-data');

    const onSatelliteDataFormReset = () => {
        formReset();
    };

    const onActionConfirm = async () => {
        if (!spotterId) {
            console.warn('No Spotter selected');
            return;
        }
        const values = formGetValues();

        try {
            await postChangeMode({
                spotterId: spotterId,
                mode: values.mode,
                messagePeriod: Number(values.messagePeriod),
            });
        } catch (err) {
            console.error(err);
        }
        onSatelliteDataFormReset();
        closeConfirmDialog();
    };

    // decomposing fields we care about out of device so the effect that initializes the form doesn't rerun on every device refresh
    const { currentRunMode, currentMode, currentMessagePeriod } = device ?? {};

    useEffect(() => {
        if (isDevicesPending) {
            return;
        }

        // Case 1: Device mode is not set
        if (!currentMode || !currentMessagePeriod) {
            formReset({ mode: '', messagePeriod: '' });
            setOutOfRangeMode({
                mode: 'Mode not set(null)',
                messagePeriod: 'N/A',
                isOutOfRange: true,
            });
            return;
        }

        // Case 2: Device is in one of the special modes, that are not typically used for public
        const regularModes = Object.keys(operatingModeToUpdateRateOptionsMap);
        if (!regularModes.includes(currentMode)) {
            // Device can change out of these modes, but not back into them
            // so we don't want to have these modes as an option in the form
            formReset({ mode: '', messagePeriod: '' });
            setOutOfRangeMode({
                mode: startCase(currentMode),
                messagePeriod: currentMessagePeriod.toString(),
                isOutOfRange: true,
            });
            return;
        }

        // Case 3: Device is in one of the regular modes
        const currentModeTyped = currentMode as DeviceRegularSatelliteModes;
        formReset({ mode: currentModeTyped, messagePeriod: currentMessagePeriod.toString() });
    }, [currentMode, currentMessagePeriod, isDevicesPending, formReset]);

    const isIdleMode = device?.currentMode === 'deployment';

    const handleDisableIdleClick = useCallback(() => {
        const targetRunMode: DeviceRunMode =
            currentRunMode && operatingModeToUpdateRateOptionsMap[currentRunMode] ? currentRunMode : 'waves';
        setValue('mode', targetRunMode, { shouldDirty: true });
        if (
            currentMessagePeriod &&
            find(operatingModeToUpdateRateOptionsMap[targetRunMode], {
                value: currentMessagePeriod.toString(),
            })
        ) {
            setValue('messagePeriod', currentMessagePeriod.toString(), { shouldDirty: true });
        } else {
            setValue('messagePeriod', operatingModeToUpdateRateOptionsMap[targetRunMode][0]?.value, {
                shouldDirty: true,
            });
        }
    }, [currentMessagePeriod, currentRunMode, setValue]);

    if (isDevicesPending || !device) {
        return (
            <Grid container gap={2}>
                <Grid size={{ xs: 12 }}>
                    <Typography variant="h5">Satellite data</Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <GenericSkeleton />
                </Grid>
            </Grid>
        );
    }

    return (
        <Stack gap={2}>
            {!isIdleMode && <Typography variant="h5">Satellite data</Typography>}
            {isIdleMode && (
                <Stack alignItems="start" gap={1}>
                    <Typography color={minorTextGrey}>
                        Your Spotter is currently in IDLE mode.
                        {!settingsLocked && <> You must switch to RUN mode to transmit data.</>}
                    </Typography>
                    {!settingsLocked && !formState.isDirty && (
                        <Button onClick={handleDisableIdleClick} variant="outlined">
                            Switch to RUN mode
                        </Button>
                    )}
                </Stack>
            )}
            {(!isIdleMode || formState.isDirty) && (
                <>
                    <Grid container spacing={4} size={{ xs: 12, sm: 8 }} justifyContent="space-between">
                        <Grid size={{ xs: 6, sm: 6 }}>
                            <FormSelect
                                name="mode"
                                control={formControl}
                                label="Mode"
                                options={deviceModes}
                                effectOnChange={(e) => {
                                    const updatedModeValue = e.target.value as DeviceRegularSatelliteModes;
                                    setValue(
                                        'messagePeriod',
                                        operatingModeToUpdateRateOptionsMap[updatedModeValue][0]?.value || ''
                                    );
                                }}
                                inputFieldProps={{
                                    id: 'satellite-data-mode',
                                    type: 'text',
                                    size: 'small',
                                    disabled: settingsLocked,
                                }}
                                controllerProps={{
                                    rules: {
                                        required: 'Field is required',
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 6 }}>
                            <FormSelect
                                name="messagePeriod"
                                control={formControl}
                                label="Update rate"
                                inputFieldProps={{
                                    id: 'satellite-data-update-rate',
                                    type: 'number',
                                    size: 'small',
                                    disabled: settingsLocked,
                                }}
                                options={operatingModeToUpdateRateOptionsMap[modeValue]}
                                controllerProps={{
                                    rules: {
                                        required: 'Field is required',
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Collapse in={formState.isDirty}>
                        <ResetUpdateButtonGroup
                            onReset={onSatelliteDataFormReset}
                            onSubmit={handleSubmit(openConfirmDialog)}
                            isResetDisabled={!formState.isDirty || formState.isSubmitting}
                            isUpdateDisabled={!formState.isDirty || formState.isSubmitting}
                            isSubmitting={formState.isSubmitting}
                            containerStyle={{ justifyContent: { xs: 'flex-end', sm: 'flex-start' }, mt: 1, mb: 0 }}
                            updateLabel="Update"
                        />
                    </Collapse>
                </>
            )}
            <ActionDialog
                isOpen={isConfirmDialogOpen}
                isLoading={pendingMutation}
                onReject={closeConfirmDialog}
                onConfirm={onActionConfirm}
                title="Are you sure you want to send these changes to your device?"
                confirmLabel="Yes, send change"
                rejectLabel="No, go back"
                message={[
                    <>
                        This will change your Spotter&apos;s settings to{' '}
                        <SatelliteModeChangeText
                            targetMode={formGetValues().mode}
                            targetMessagePeriod={Number(formGetValues().messagePeriod)}
                        />
                        . This change may affect your subscription data usage. View our{' '}
                        <Link
                            href="https://sofarocean.notion.site/Spotter-Data-Modes-e78a76f5f94e49f58bfd68845113c4a7"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Spotter Data Modes documentation
                        </Link>{' '}
                        for more details.
                    </>,
                    <>
                        This change may take several hours to complete, during which your Spotter will continue to send
                        data using its current settings. You will not be able to make changes to your Spotter&apos;s
                        data transmission settings until this process is complete.
                    </>,
                ]}
            />
        </Stack>
    );
};
